/**
 * Sessions Report Grid Column Definitions
 * Matches Flutter's session report with column group toggles
 *
 * HIVE-6245 #12: Always-visible columns remain visible regardless of column group toggles
 */

import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { SessionGridRow } from '../types/session-types';
import { TariffLevelMapping } from '../types/api-types';
import { formatDateTime, formatDuration } from '../utils/date-utils';

/**
 * Always-visible column IDs (HIVE-6245 #12)
 * These columns remain visible regardless of which column groups are enabled
 */
export const ALWAYS_VISIBLE_COLUMNS = [
  'sessionId',
  'startTime',
  'durationInSeconds',
  'totalEnergy',
  'locationName',
  'chargePointNumber',
  'connectorId',
  'startSource',
] as const;

/**
 * Format energy value
 */
function formatEnergy(energy: number | null | undefined): string {
  if (energy === null || energy === undefined || isNaN(energy)) {
    return '--';
  }
  return energy.toFixed(2);
}

/**
 * Format currency value
 */
function formatCurrency(
  value: number | null | undefined,
  currency?: string | null
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '--';
  }
  return value.toFixed(2);
}

/**
 * Column group definitions matching Flutter's checkbox toggles (HIVE-6300)
 *
 * Flutter groups:
 * - Session Info: Meter Start/Stop
 * - Charge Point: Location Usage, Physical Reference, Charge Point ID
 * - Account: Account Name, ID, External Code, Token ID, Display Number
 * - Fees (Cost): Energy Cost, Time Fee, Connection Fee, Total Payment
 * - VAT Include: VAT Cost, VAT % (conditional - only if Fees is ON)
 * - Payment Info: EV Tier Pricing, Nayax Transaction ID
 * - PTP Info: OCPI EMSP Name, Device Serial, Spark Machine ID
 * - Buy Info: Energy Buy Cost, Profit (conditional - only if Payment Info is ON)
 */
export type ColumnGroup =
  | 'sessionInfo'   // Meter Start/Stop
  | 'chargePoint'   // Location Usage, Physical Reference, Charge Point ID
  | 'account'       // Account details
  | 'cost'          // Energy cost, fees, total payment
  | 'vatInclude'    // VAT breakdown (conditional - only if Cost is ON)
  | 'paymentInfo'   // EV Tier, Nayax Txn ID
  | 'ptpInfo'       // OCPI, Device Serial, Spark Machine
  | 'buyInfo';      // Energy buy cost, profit

export interface ColumnGroupConfig {
  id: ColumnGroup;
  label: string;
  labelEs: string;
  columns: string[];
  defaultEnabled: boolean;
  /** If set, this group's checkbox only appears when the parent group is enabled */
  dependsOn?: ColumnGroup;
}

export const columnGroups: ColumnGroupConfig[] = [
  {
    id: 'sessionInfo',
    label: 'Session Info',
    labelEs: 'Info Sesión',
    columns: ['meterStart', 'meterStop', 'stopReason', 'location'],
    defaultEnabled: true,
  },
  {
    id: 'chargePoint',
    label: 'Charge Point',
    labelEs: 'Punto de Carga',
    columns: [
      'machineUsage',
      'physicalReference',
      'chargePointId',
      'locationName',
      'chargePointNumber',
      'connectorId',
      'locationId',
    ],
    defaultEnabled: true,
  },
  {
    id: 'ptpInfo',
    label: 'PTP Info',
    labelEs: 'Info PTP',
    columns: ['isOcpi', 'ocpiEmspName', 'deviceSerial', 'machineModelName', 'sparkMachineId'],  // HIVE-6618: added machineModelName
    defaultEnabled: false,
  },
  {
    id: 'account',
    label: 'Account',
    labelEs: 'Cuenta',
    columns: ['accountId', 'accountName', 'accountExternalCode', 'tokenId', 'tokenDisplayNumber', 'cardType', 'rfid', 'startSource'],  // HIVE-6307: added accountId
    defaultEnabled: true,
  },
  {
    id: 'cost',
    label: 'Fees',
    labelEs: 'Tarifas',
    columns: [
      // Energy Fee group
      'energyCostExcVat', 'energyVat', 'energyCostIncVat',
      // Time Fee group
      'timeFeeWithoutVat', 'timeFeeVat', 'timeFee',
      // Flat Fee group
      'connectionFeeWithoutVat', 'connectionFeeVat', 'connectionFee',
      // Idle Fee group
      'idleFeeWithoutVat', 'idleFeeVat', 'idleFee',
      // Tariff levels (L1-L9) - dynamic columns will be added here
      'l1UnitCost', 'l1Energy', 'l1Cost',
      'l2UnitCost', 'l2Energy', 'l2Cost',
      'l3UnitCost', 'l3Energy', 'l3Cost',
      'l4UnitCost', 'l4Energy', 'l4Cost',
      'l5UnitCost', 'l5Energy', 'l5Cost',
      'l6UnitCost', 'l6Energy', 'l6Cost',
      'l7UnitCost', 'l7Energy', 'l7Cost',
      'l8UnitCost', 'l8Energy', 'l8Cost',
      'l9UnitCost', 'l9Energy', 'l9Cost',
    ],
    defaultEnabled: true,
  },
  {
    id: 'vatInclude',
    label: 'VAT Include',
    labelEs: 'IVA Incluido',
    columns: [], // No columns - checkbox exists for Flutter compatibility but doesn't show VAT/VAT% columns
    defaultEnabled: false,
    dependsOn: 'cost', // Only visible when Fees (Cost) is enabled
  },
  {
    id: 'paymentInfo',
    label: 'Payment Info',
    labelEs: 'Info de Pago',
    columns: [
      'paymentId',  // HIVE-6307: paymentId now shows nayaxTransactionId
      // Total Fees (before discount)
      'totalPaymentCostExcVat', 'totalPaymentVat', 'totalPaymentCostIncVat',
      // Settlement group (final settlement value)
      'settlementExcVat', 'settlementVat', 'settlementIncVat',
      // Discount group (includes community info)
      'communityName', 'communityValue',
      'discountExcVat', 'discountVat', 'discountIncVat',
      // Total Cost group (after discount)
      'totalPaymentAfterDiscountExcVat', 'totalPaymentAfterDiscountVat', 'totalPaymentAfterDiscountIncVat',
    ],
    defaultEnabled: true,
  },
  {
    id: 'buyInfo',
    label: 'Buy Info',
    labelEs: 'Info Compra',
    columns: ['utilityEnergyCost', 'energyIncome', 'totalIncome'],
    defaultEnabled: false,
    dependsOn: 'paymentInfo', // Only visible when Payment Info is enabled
  },
];

/**
 * All column definitions for Sessions Report Grid
 * Matches Flutter columns with new group assignments (HIVE-6300)
 */
export const sessionsGridColumns: ColumnDef<SessionGridRow>[] = [
  // Always-visible columns (no group - always shown)
  {
    id: 'sessionId',
    accessorKey: 'sessionId',
    header: 'Session ID',
    size: 100,
    cell: ({ getValue }) => (
      <span className="font-mono text-sm">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { /* always visible */ },
  },
  {
    id: 'startTime',
    accessorKey: 'startTime',
    header: 'Started At',
    size: 160,
    cell: ({ getValue }) => (
      <span className="text-sm">{formatDateTime(getValue<string>())}</span>
    ),
    enableSorting: true,
    meta: { /* always visible */ },
  },
  // HIVE-6307: Ended At column after Started At
  {
    id: 'stopTime',
    accessorKey: 'stopTime',
    header: 'Ended At',
    size: 160,
    cell: ({ getValue }) => (
      <span className="text-sm">{formatDateTime(getValue<string | null>())}</span>
    ),
    enableSorting: true,
    meta: { /* always visible */ },
  },
  {
    id: 'durationInSeconds',
    accessorKey: 'durationInSeconds',
    header: 'Duration',
    size: 100,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono">
        {formatDuration(getValue<number | null>())}
      </span>
    ),
    enableSorting: true,
    meta: { /* always visible */ },
  },

  // Session Info group columns (Meter Start/Stop)
  {
    id: 'meterStart',
    accessorKey: 'meterStart',
    header: 'Meter Start',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'sessionInfo' },
  },
  {
    id: 'meterStop',
    accessorKey: 'meterStop',
    header: 'Meter Stop',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'sessionInfo' },
  },
  {
    id: 'totalEnergy',
    accessorKey: 'totalEnergy',
    header: 'Energy [kWh]',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm">
        {formatEnergy(getValue<number | null>())}
      </span>
    ),
    enableSorting: true,
    meta: { align: 'left' /* always visible */ },
  },
  {
    id: 'stopReason',
    accessorKey: 'stopReason',
    header: 'Stop Reason',
    size: 120,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<string>() || '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'sessionInfo' },
  },

  // Charge Point group columns
  // HIVE-6307: Renamed from "Machine Name" to "Charging Station Name"
  {
    id: 'locationName',
    accessorKey: 'locationName',
    header: 'Charging Station Name',
    size: 200,
    minSize: 200,
    cell: ({ getValue }) => (
      <span className="text-sm truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint' },
  },
  // HIVE-6307: Renamed from "CP Number" to "Charging Station Number"
  {
    id: 'chargePointNumber',
    accessorKey: 'chargePointNumber',
    header: 'Charging Station Number',
    size: 200,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint', align: 'center' },
  },
  {
    id: 'connectorId',
    accessorKey: 'connectorId',
    header: 'Connector ID',
    size: 100,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint', align: 'center' },
  },
  {
    id: 'location',
    accessorKey: 'kioskActorHierarchy',
    header: 'Location',
    size: 100,
    cell: ({ getValue }) => {
      const fullPath = getValue<string>() || '';
      // Extract last 2 parts of hierarchy like Flutter does
      // e.g., "Nayax Demo / Sonol EVI / Spark Machines / Automation Stub Billing /"
      // becomes "Spark Machines / Automation Stub Billing"
      const parts = fullPath.split(' / ').filter(p => p.trim());
      const displayValue = parts.length > 2
        ? parts.slice(-2).join(' / ')
        : fullPath || '--';

      return (
        <div className="text-sm truncate w-full overflow-hidden" title={fullPath}>
          {displayValue}
        </div>
      );
    },
    enableSorting: true,
    meta: { group: 'chargePoint', align: 'center' },
  },
  {
    id: 'locationId',
    accessorKey: 'locationId',
    header: 'Location ID',
    size: 100,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint', align: 'center' },
  },
  {
    id: 'machineUsage',
    accessorKey: 'machineUsage',
    header: 'Usage Type',
    size: 100,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<string>() || '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint' },
  },
  {
    id: 'physicalReference',
    accessorKey: 'physicalReference',
    header: 'Physical Ref',
    size: 140,
    cell: ({ getValue }) => (
      <span className="text-sm truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint' },
  },
  {
    id: 'chargePointId',
    accessorKey: 'chargePointId',
    header: 'CP ID',
    size: 120,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint', align: 'center' },
  },
  {
    id: 'startSource',
    accessorKey: 'startSource',
    header: 'Start Source',
    size: 140,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<string>() || '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'chargePoint' },
  },

  // PTP Info group columns (OCPI, Device Serial, Spark Machine)
  {
    id: 'isOcpi',
    accessorKey: 'isOcpi',
    header: 'OCPI',
    size: 80,
    cell: ({ getValue }) => {
      const value = getValue<boolean>();
      return (
        <span className={`text-sm ${value ? 'text-green-600' : 'text-gray-400'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    },
    enableSorting: true,
    meta: { group: 'ptpInfo', align: 'center' },
  },
  {
    id: 'ocpiEmspName',
    accessorKey: 'ocpiEmspName',
    header: 'EMSP',
    size: 100,
    cell: ({ getValue }) => (
      <span className="text-sm truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'ptpInfo' },
  },
  {
    id: 'prepaidCard',
    accessorKey: 'prepaidCard',
    header: 'Prepaid Card',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'ptpInfo', align: 'center' },
  },
  {
    id: 'deviceSerial',
    accessorKey: 'deviceSerial',
    header: 'Device Serial',
    size: 120,
    minSize: 120,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'ptpInfo', align: 'center' },
  },
  // HIVE-6618: Device Type column
  {
    id: 'machineModelName',
    accessorKey: 'machineModelName',
    header: 'Device Type',
    size: 150,
    cell: ({ getValue }) => (
      <span className="text-sm truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'ptpInfo' },
  },
  {
    id: 'sparkMachineId',
    accessorKey: 'sparkMachineId',
    header: 'Spark Machine ID',
    size: 150,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'ptpInfo', align: 'center' },
  },

  // Account group columns
  // Account Name before Account ID
  {
    id: 'accountName',
    accessorKey: 'accountName',
    header: 'Account Name',
    size: 120,
    cell: ({ getValue }) => (
      <span className="text-sm truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'account' },
  },
  {
    id: 'accountId',
    accessorKey: 'accountId',
    header: 'Account ID',
    size: 100,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'account' },
  },
  {
    id: 'accountExternalCode',
    accessorKey: 'accountExternalCode',
    header: 'External Code',
    size: 120,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'account' },
  },
  {
    id: 'isExternalPayment',
    accessorKey: 'isExternalPayment',
    header: 'External Payment',
    size: 130,
    cell: ({ getValue }) => {
      const value = getValue<boolean | null>();
      if (value === null || value === undefined) return <span className="text-sm">--</span>;
      return (
        <span className="text-sm">{value ? 'true' : 'false'}</span>
      );
    },
    enableSorting: true,
    meta: { group: 'account' },
  },
  {
    id: 'tokenId',
    accessorKey: 'tokenId',
    header: 'Token ID',
    size: 140,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'account' },
  },
  {
    id: 'tokenDisplayNumber',
    accessorKey: 'tokenDisplayNumber',
    header: 'Token Display',
    size: 140,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'account' },
  },
  {
    id: 'cardType',
    accessorKey: 'cardType',
    header: 'Card Type',
    size: 100,
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue<string>() || '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'account' },
  },
  {
    id: 'rfid',
    accessorKey: 'rfid',
    header: 'RFID',
    size: 140,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono truncate" title={getValue<string>() || ''}>
        {getValue<string>() || '--'}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'account' },
  },

  // Energy Fee Group (energy cost only, not including connection/time/idle fees)
  {
    id: 'energyFeeGroup',
    header: 'Energy Fee',
    meta: { group: 'cost', align: 'center' },
    columns: [
      {
        id: 'energyCostExcVat',
        accessorKey: 'cost',
        header: 'Excl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'energyVat',
        accessorKey: 'vatCost',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'energyCostIncVat',
        accessorFn: (row: SessionGridRow) => {
          const cost = row.cost ?? 0;
          const vatCost = row.vatCost ?? 0;
          return cost + vatCost;
        },
        header: 'Incl. VAT',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span className="text-sm">{formatCurrency(value)}</span>;
        },
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
    ],
  },
  // Time Fee Group (with sub-columns)
  {
    id: 'timeFeeGroup',
    header: 'Time Fee',
    meta: { group: 'cost', align: 'center' },
    columns: [
      {
        id: 'timeFeeWithoutVat',
        accessorKey: 'timeFeeWithoutVat',
        header: 'Excl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'timeFeeVat',
        accessorKey: 'timeFeeVat',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'timeFee',
        accessorKey: 'timeFee',
        header: 'Incl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
    ],
  },
  // Flat Fee Group (with sub-columns)
  {
    id: 'flatFeeGroup',
    header: 'Flat Fee',
    meta: { group: 'cost', align: 'center' },
    columns: [
      {
        id: 'connectionFeeWithoutVat',
        accessorKey: 'connectionFeeWithoutVat',
        header: 'Excl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'connectionFeeVat',
        accessorKey: 'connectionFeeVat',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'connectionFee',
        accessorKey: 'connectionFee',
        header: 'Incl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
    ],
  },
  // Idle Fee Group (with sub-columns)
  {
    id: 'idleFeeGroup',
    header: 'Idle Fee',
    meta: { group: 'cost', align: 'center' },
    columns: [
      {
        id: 'idleFeeWithoutVat',
        accessorKey: 'idleFeeWithoutVat',
        header: 'Excl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'idleFeeVat',
        accessorKey: 'idleFeeVat',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
      {
        id: 'idleFee',
        accessorKey: 'idleFee',
        header: 'Incl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'cost', align: 'center' },
      },
    ],
  },
  // Total Fees Group (total payment before discount - matching Flutter)
  {
    id: 'totalFeesGroup',
    header: 'Total Fees',
    meta: { group: 'paymentInfo', align: 'center' },
    columns: [
      {
        id: 'totalPaymentCostExcVat',
        accessorKey: 'totalPaymentCostExcVat',
        header: 'Excl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'totalPaymentVat',
        accessorKey: 'totalPaymentVat',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'totalPaymentCostIncVat',
        accessorKey: 'totalPaymentCostIncVat',
        header: 'Incl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm font-medium">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
    ],
  },
  // Settlement Group (with sub-columns) - Shows total cost after discount
  {
    id: 'settlementGroup',
    header: 'Settlement',
    meta: { group: 'paymentInfo', align: 'center' },
    columns: [
      {
        id: 'settlementExcVat',
        accessorKey: 'totalPaymentAfterDiscountExcVat',
        header: 'Excl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'settlementVat',
        accessorKey: 'totalPaymentAfterDiscountVat',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'settlementIncVat',
        accessorKey: 'settlementValue',
        header: 'Incl. VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">{formatCurrency(getValue<number | null>())}</span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
    ],
  },
  // Discount Group (with sub-columns matching Flutter)
  {
    id: 'discountGroup',
    header: 'Discount',
    meta: { group: 'paymentInfo', align: 'center' },
    columns: [
      {
        id: 'communityName',
        accessorKey: 'communityName',
        header: 'Community Type',
        size: 150,
        cell: ({ row }) => {
          const value = row.original.communityName;
          return (
            <span className="text-sm">
              {value ?? '--'}
            </span>
          );
        },
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'communityValue',
        accessorKey: 'communityValue',
        header: 'Community Info',
        size: 150,
        cell: ({ row }) => {
          const value = row.original.communityValue;
          return (
            <span className="text-sm" title={value || ''}>
              {value || '--'}
            </span>
          );
        },
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'discountExcVat',
        accessorKey: 'discountExcVat',
        header: 'Exc VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">
            {formatCurrency(getValue<number | null>())}
          </span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'discountVat',
        accessorKey: 'discountVat',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">
            {formatCurrency(getValue<number | null>())}
          </span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'discountIncVat',
        accessorKey: 'discountIncVat',
        header: 'Inc VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm font-medium">
            {formatCurrency(getValue<number | null>())}
          </span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
    ],
  },
  // Total Cost Group (with sub-columns matching Flutter)
  {
    id: 'totalCostGroup',
    header: 'Total Cost',
    meta: { group: 'paymentInfo', align: 'center' },
    columns: [
      {
        id: 'totalPaymentAfterDiscountExcVat',
        accessorKey: 'totalPaymentAfterDiscountExcVat',
        header: 'Exc VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm">
            {formatCurrency(getValue<number | null>())}
          </span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'totalPaymentAfterDiscountVat',
        accessorKey: 'totalPaymentAfterDiscountVat',
        header: 'VAT',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-sm">
            {formatCurrency(getValue<number | null>())}
          </span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
      {
        id: 'totalPaymentAfterDiscountIncVat',
        accessorKey: 'totalPaymentAfterDiscountIncVat',
        header: 'Inc VAT',
        size: 100,
        cell: ({ getValue }) => (
          <span className="text-sm font-medium">
            {formatCurrency(getValue<number | null>())}
          </span>
        ),
        enableSorting: true,
        meta: { group: 'paymentInfo', align: 'center' },
      },
    ],
  },

  // Payment Info group columns
  // HIVE-6307: Payment ID column now shows nayaxTransactionId value
  {
    id: 'paymentId',
    accessorKey: 'nayaxTransactionId',
    header: 'Nayax Transaction ID',
    size: 150,
    cell: ({ getValue }) => (
      <span className="text-sm font-mono">{getValue<number>() ?? '--'}</span>
    ),
    enableSorting: true,
    meta: { group: 'paymentInfo', align: 'center' },
  },

  // Buy Info group columns (matching Flutter) - depends on paymentInfo
  {
    id: 'utilityEnergyCost',
    accessorKey: 'energyBuyCost',
    header: 'Total Buy Cost',
    size: 150,
    cell: ({ getValue }) => (
      <span className="text-sm">
        {formatCurrency(getValue<number | null>())}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'buyInfo', align: 'center' },
  },
  {
    id: 'energyIncome',
    accessorKey: 'energyProfit',
    header: 'Gross Profit',
    size: 140,
    cell: ({ getValue }) => (
      <span className="text-sm">
        {formatCurrency(getValue<number | null>())}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'buyInfo', align: 'center' },
  },
  {
    id: 'totalIncome',
    accessorKey: 'totalProfit',
    header: 'Total Income Excl. VAT',
    size: 140,
    cell: ({ getValue }) => (
      <span className="text-sm font-medium">
        {formatCurrency(getValue<number | null>())}
      </span>
    ),
    enableSorting: true,
    meta: { group: 'buyInfo', align: 'center' },
  },
];

/**
 * Default sorting configuration - most recent sessions first
 */
export const defaultSorting = [
  {
    id: 'startTime',
    desc: true,
  },
];

/**
 * Get default column visibility based on enabled column groups
 * HIVE-6245 #12: Always-visible columns are shown regardless of group settings
 */
export function getDefaultColumnVisibility(
  enabledGroups: ColumnGroup[] = columnGroups
    .filter((g) => g.defaultEnabled)
    .map((g) => g.id),
  columns: any[] = sessionsGridColumns
): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  const alwaysVisibleSet = new Set<string>(ALWAYS_VISIBLE_COLUMNS);

  const processColumn = (col: any) => {
    const colId = col.id as string;
    const group = (col.meta as any)?.group as ColumnGroup | undefined;

    // Always-visible columns are always shown (HIVE-6245 #12)
    if (alwaysVisibleSet.has(colId)) {
      visibility[colId] = true;
    } else {
      // Other columns depend on their group being enabled
      visibility[colId] = group ? enabledGroups.includes(group) : true;
    }

    // Process child columns if this is a grouped column
    if (col.columns && Array.isArray(col.columns)) {
      col.columns.forEach((childCol: any) => processColumn(childCol));
    }
  };

  columns.forEach((col) => processColumn(col));

  return visibility;
}

/**
 * Default column visibility with default groups enabled
 */
export const defaultColumnVisibility = getDefaultColumnVisibility();

/**
 * Available columns for reference
 */
export const availableColumns = sessionsGridColumns.map((col) => ({
  id: col.id as string,
  header: col.header as string,
  group: (col.meta as any)?.group || 'other',
}));

/**
 * Get sessions grid columns with dynamic tariff level filtering
 * Adds tariff level columns based on data presence and filters out empty fee groups
 * @param tariffLevels - Tariff level mappings from API
 * @param data - Session data for filtering
 * @param vatIncludeEnabled - Whether VAT Include checkbox is enabled (affects tariff price display)
 * @param t - Translation function from i18next (optional, uses fallback if not provided)
 * @returns Column definitions for the grid
 */
export function getSessionsGridColumns(
  tariffLevels: TariffLevelMapping[] = [],
  data: SessionGridRow[] = [],
  vatIncludeEnabled: boolean = false,
  t?: TFunction
): ColumnDef<SessionGridRow>[] {
  // Filter tariff levels that have data across any session (excluding zero values)
  const activeTariffLevels = tariffLevels.filter(level => {
    const levelNum = level.index; // API uses 1-based: index 1 = L1, index 2 = L2
    return data.some(row => {
      const unitCost = row[`l${levelNum}UnitCost` as keyof SessionGridRow];
      const energy = row[`l${levelNum}Energy` as keyof SessionGridRow];
      const cost = row[`l${levelNum}Cost` as keyof SessionGridRow];
      return (unitCost != null && unitCost !== 0) ||
             (energy != null && energy !== 0) ||
             (cost != null && cost !== 0);
    });
  });

  // Create tariff level grouped columns (matching Flutter order)
  const tariffColumns: ColumnDef<SessionGridRow>[] = activeTariffLevels.map(level => {
    const levelNum = level.index;
    return {
      id: `tariff-l${levelNum}`,
      header: level.name,
      meta: { group: 'cost', align: 'center' },
      columns: [
        {
          id: `l${levelNum}UnitCost`,
          accessorKey: `l${levelNum}UnitCost` as keyof SessionGridRow,
          header: 'Sell Unit Price',
          size: 100,
          cell: ({ getValue, row }) => {
            let price = getValue<number | null>();
            // When VAT Include is enabled, show price including VAT
            if (vatIncludeEnabled && price != null && price !== 0) {
              const vatRate = row.original.vatRate ?? 0;
              price = price * (1 + vatRate / 100);
            }
            return <span className="text-sm">{formatCurrency(price)}</span>;
          },
          enableSorting: true,
          meta: { group: 'cost', align: 'center' },
        },
        {
          id: `l${levelNum}Energy`,
          accessorKey: `l${levelNum}Energy` as keyof SessionGridRow,
          header: 'Energy [kWh]',
          size: 110,
          cell: ({ getValue }) => {
            const value = getValue<number | null>();
            return <span className="text-sm">{value != null ? value.toFixed(2) : '--'}</span>;
          },
          enableSorting: true,
          meta: { group: 'cost', align: 'center' },
        },
        {
          id: `l${levelNum}Cost`,
          accessorKey: `l${levelNum}Cost` as keyof SessionGridRow,
          header: 'Sell Cost',
          size: 100,
          cell: ({ getValue, row }) => {
            let price = getValue<number | null>();
            // When VAT Include is enabled, show price including VAT
            if (vatIncludeEnabled && price != null && price !== 0) {
              const vatRate = row.original.vatRate ?? 0;
              price = price * (1 + vatRate / 100);
            }
            return <span className="text-sm">{formatCurrency(price)}</span>;
          },
          enableSorting: true,
          meta: { group: 'cost', align: 'center' },
        },
      ],
    };
  });

  // Check if fee groups have data (excluding zero values)
  const hasEnergyFeeData = data.some(row =>
    (row.cost != null && row.cost !== 0) ||
    (row.vatCost != null && row.vatCost !== 0)
  );
  const hasFlatFeeData = data.some(row =>
    (row.connectionFee != null && row.connectionFee !== 0) ||
    (row.connectionFeeWithoutVat != null && row.connectionFeeWithoutVat !== 0) ||
    (row.connectionFeeVat != null && row.connectionFeeVat !== 0)
  );
  const hasTimeFeeData = data.some(row =>
    (row.timeFee != null && row.timeFee !== 0) ||
    (row.timeFeeWithoutVat != null && row.timeFeeWithoutVat !== 0) ||
    (row.timeFeeVat != null && row.timeFeeVat !== 0)
  );
  const hasIdleFeeData = data.some(row =>
    (row.idleFee != null && row.idleFee !== 0) ||
    (row.idleFeeWithoutVat != null && row.idleFeeWithoutVat !== 0) ||
    (row.idleFeeVat != null && row.idleFeeVat !== 0)
  );
  const hasTotalFeesData = data.some(row =>
    (row.totalPaymentCostExcVat != null && row.totalPaymentCostExcVat !== 0) ||
    (row.totalPaymentVat != null && row.totalPaymentVat !== 0) ||
    (row.totalPaymentCostIncVat != null && row.totalPaymentCostIncVat !== 0)
  );
  const hasDiscountData = data.some(row =>
    (row.communityName != null && row.communityName !== '') ||
    (row.communityValue != null && row.communityValue !== '') ||
    (row.discountExcVat != null && row.discountExcVat !== 0) ||
    (row.discountVat != null && row.discountVat !== 0) ||
    (row.discountIncVat != null && row.discountIncVat !== 0)
  );
  const hasTotalCostData = data.some(row =>
    row.totalPaymentAfterDiscountExcVat != null ||
    row.totalPaymentAfterDiscountVat != null ||
    row.totalPaymentAfterDiscountIncVat != null
  );

  // Filter columns to only include fee groups with data
  let columns = sessionsGridColumns.filter(col => {
    if (col.id === 'energyFeeGroup' && !hasEnergyFeeData) return false;
    if (col.id === 'flatFeeGroup' && !hasFlatFeeData) return false;
    if (col.id === 'timeFeeGroup' && !hasTimeFeeData) return false;
    if (col.id === 'idleFeeGroup' && !hasIdleFeeData) return false;
    if (col.id === 'totalFeesGroup' && !hasTotalFeesData) return false;
    if (col.id === 'discountGroup' && !hasDiscountData) return false;
    if (col.id === 'totalCostGroup' && !hasTotalCostData) return false;
    return true;
  });

  // Find the position to insert tariff columns (before Energy Fee)
  const energyFeeIndex = columns.findIndex(col => col.id === 'energyFeeGroup');

  // Insert tariff columns before Energy Fee (at the beginning of fees section)
  const insertIndex = energyFeeIndex >= 0 ? energyFeeIndex : 0;

  columns.splice(insertIndex, 0, ...tariffColumns);

  // Apply translations to column headers if translation function is provided
  if (t) {
    columns = columns.map(col => {
      // Map of column IDs to Flutter translation keys
      const translationMap: Record<string, string> = {
        sessionId: 'text_session_tab_transaction_id',
        durationInSeconds: 'text_session_tab_duration',
        meterStart: 'text_session_tab_deviceStartDate',
        meterStop: 'text_session_tab_end',
        totalEnergy: 'text_session_tab_energy',
        accountId: 'text_session_tab_account_id',
      };

      const translationKey = translationMap[col.id || ''];
      if (translationKey) {
        return {
          ...col,
          header: t(translationKey, { defaultValue: col.header as string }),
        };
      }

      // For grouped columns (with sub-columns), apply translations to children
      // Type assertion for grouped columns
      const groupedCol = col as any;
      if (groupedCol.columns && Array.isArray(groupedCol.columns)) {
        return {
          ...col,
          columns: groupedCol.columns.map((subCol: any) => {
            const subTranslationKey = translationMap[subCol.id || ''];
            if (subTranslationKey) {
              return {
                ...subCol,
                header: t(subTranslationKey, { defaultValue: subCol.header as string }),
              };
            }
            return subCol;
          }),
        };
      }

      return col;
    });
  }

  return columns;
}
